import { useState, useEffect } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Check, ExternalLink, Loader2, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabaseClient'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const BUSINESS_ROLES = [
  { value: 'business_owner', label: 'Owner' },
  { value: 'business_manager', label: 'Manager' },
  { value: 'business_user', label: 'User' },
  { value: 'business_viewer', label: 'Viewer' },
] as const

interface MembershipRow {
  id: string
  user_id: string
  role: string
  status: string
}

interface PersonWithAuth {
  id: string
  auth_user_id: string
  first_name: string
  last_name: string
  email: string
}

export default function PortalCompanyDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { canSeeClients, isSiteAdmin, isLoading: roleLoading, isError: roleError } = usePlatformRoleContext()
  const queryClient = useQueryClient()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [addFormKey, setAddFormKey] = useState(0)
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [country, setCountry] = useState('')
  const [entityName, setEntityName] = useState('')
  const [taxId, setTaxId] = useState('')

  const { data: business, isLoading: businessLoading, error: businessError } = useQuery({
    queryKey: ['business', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, website_url, country, entity_name, tax_id')
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!id && canSeeClients,
  })

  useEffect(() => {
    if (business) {
      setName(business.name)
      setWebsite(business.website_url ?? '')
      setCountry(business.country ?? '')
      setEntityName(business.entity_name ?? '')
      setTaxId(business.tax_id ?? '')
    }
  }, [business])

  const { data: memberships, isLoading: membershipsLoading } = useQuery({
    queryKey: ['business-memberships', id],
    queryFn: async (): Promise<MembershipRow[]> => {
      if (!id) return []
      const { data, error } = await supabase
        .from('business_memberships')
        .select('id, user_id, role, status')
        .eq('business_id', id)
      if (error) throw error
      return (data ?? []) as MembershipRow[]
    },
    enabled: !!id && canSeeClients,
  })

  const { data: membersPeople } = useQuery({
    queryKey: ['people-by-auth', memberships?.map((m) => m.user_id) ?? []],
    queryFn: async (): Promise<PersonWithAuth[]> => {
      const userIds = memberships?.map((m) => m.user_id).filter(Boolean) ?? []
      if (userIds.length === 0) return []
      const { data, error } = await supabase
        .from('people')
        .select('id, auth_user_id, first_name, last_name, email')
        .in('auth_user_id', userIds)
      if (error) throw error
      return (data ?? []) as PersonWithAuth[]
    },
    enabled: !!memberships && memberships.length > 0,
  })

  const { data: availablePeople } = useQuery({
    queryKey: ['people-with-auth', id, memberships?.map((m) => m.user_id) ?? []],
    queryFn: async (): Promise<PersonWithAuth[]> => {
      const memberUserIds = new Set(memberships?.map((m) => m.user_id) ?? [])
      const { data, error } = await supabase
        .from('people')
        .select('id, auth_user_id, first_name, last_name, email')
        .not('auth_user_id', 'is', null)
      if (error) throw error
      return ((data ?? []) as PersonWithAuth[]).filter((p) => p.auth_user_id && !memberUserIds.has(p.auth_user_id))
    },
    enabled: !!id && canSeeClients,
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: { name: string; website_url?: string; country?: string; entity_name?: string; tax_id?: string }) => {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: payload.name,
          website_url: payload.website_url || null,
          country: payload.country || null,
          entity_name: payload.entity_name || null,
          tax_id: payload.tax_id || null,
        })
        .eq('id', id!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', id] })
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      toast.success('Company updated')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const addMemberMutation = useMutation({
    mutationFn: async ({ user_ids, role }: { user_ids: string[]; role: string }) => {
      if (user_ids.length === 0) return
      const rows = user_ids.map((user_id) => ({
        business_id: id!,
        user_id,
        role,
        status: 'active',
      }))
      const { error } = await supabase.from('business_memberships').insert(rows)
      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['business-memberships', id] })
      queryClient.invalidateQueries({ queryKey: ['people-with-auth', id] })
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      setAddFormKey((k) => k + 1)
      const count = variables.user_ids.length
      toast.success(count === 1 ? 'User added to company' : `${count} users added to company`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const updateRoleMutation = useMutation({
    mutationFn: async ({ membershipId, role }: { membershipId: string; role: string }) => {
      const { error } = await supabase
        .from('business_memberships')
        .update({ role })
        .eq('id', membershipId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-memberships', id] })
      toast.success('Role updated')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const deleteCompanyMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('businesses').delete().eq('id', id!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      toast.success('Company deleted')
      navigate('/portal/businesses')
    },
    onError: (err: Error) => {
      toast.error(err.message)
      setDeleteOpen(false)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (membershipId: string) => {
      const { error } = await supabase.from('business_memberships').delete().eq('id', membershipId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-memberships', id] })
      queryClient.invalidateQueries({ queryKey: ['people-with-auth', id] })
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      toast.success('User removed from company')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const handleSaveDetails = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    updateMutation.mutate({
      name: name.trim(),
      website_url: website.trim() || undefined,
      country: country.trim() || undefined,
      entity_name: entityName.trim() || undefined,
      tax_id: taxId.trim() || undefined,
    })
  }

  const getPersonForUser = (userId: string) =>
    membersPeople?.find((p) => p.auth_user_id === userId)

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (roleError || !canSeeClients || !isSiteAdmin) {
    return <Navigate to="/portal" replace />
  }

  if (businessError || (!businessLoading && !business)) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <p className="text-white/80">Company not found.</p>
        <Link to="/portal/businesses" className="text-white/60 hover:text-white text-sm mt-2 inline-block">
          ← Back to Companies
        </Link>
      </div>
    )
  }

  if (businessLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
      >
        <div>
          <Link
            to="/portal/businesses"
            className="text-sm text-white/60 hover:text-white mb-2 inline-block"
          >
            ← Back to Companies
          </Link>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">{business!.name}</h1>
          <p className="text-white/60 text-sm mt-1">Edit company details and manage linked users.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {business!.website_url && (
            <a
              href={business!.website_url.startsWith('http') ? business!.website_url : `https://${business!.website_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Website
            </a>
          )}
          <AlertDialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialog.Trigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete company
              </button>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
              <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
              <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/15 bg-slate-900 p-6 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
                <AlertDialog.Title className="text-lg font-semibold text-white">
                  Delete company
                </AlertDialog.Title>
                <AlertDialog.Description className="text-sm text-white/60 mt-2">
                  Are you sure you want to delete <strong className="text-white">{business!.name}</strong>? This will
                  remove all linked users and cannot be undone.
                </AlertDialog.Description>
                <div className="flex gap-2 mt-6">
                  <AlertDialog.Cancel asChild>
                    <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10">
                      Cancel
                    </Button>
                  </AlertDialog.Cancel>
                  <Button
                    onClick={() => deleteCompanyMutation.mutate()}
                    disabled={deleteCompanyMutation.isPending}
                    className="bg-amber-500 text-primary hover:bg-amber-600"
                  >
                    {deleteCompanyMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Delete'
                    )}
                  </Button>
                </div>
              </AlertDialog.Content>
            </AlertDialog.Portal>
          </AlertDialog.Root>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
      >
        <h2 className="text-sm font-heading uppercase tracking-wide text-white/70 mb-4">Company details</h2>
        <form onSubmit={handleSaveDetails} className="space-y-4">
          <div>
            <Label htmlFor="company-name" className="text-white/80">
              Name <span className="text-amber-400">*</span>
            </Label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company name"
              required
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={updateMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-website" className="text-white/80">
              Website
            </Label>
            <Input
              id="company-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={updateMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-country" className="text-white/80">
              Country
            </Label>
            <Input
              id="company-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Country"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={updateMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-entity-name" className="text-white/80">
              Entity name
            </Label>
            <Input
              id="company-entity-name"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="Full legal name of the business"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={updateMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-tax-id" className="text-white/80">
              Tax ID
            </Label>
            <Input
              id="company-tax-id"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="Tax ID or government business ID"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={updateMutation.isPending}
            />
          </div>
          <Button
            type="submit"
            disabled={updateMutation.isPending || !name.trim()}
            className="bg-white text-accent hover:bg-white/90"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </form>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
      >
        <h2 className="text-sm font-heading uppercase tracking-wide text-white/70 mb-4">Linked users</h2>
        <p className="text-white/60 text-sm mb-4">Users with access to this company and their roles.</p>

        {membershipsLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/50" />
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              {(memberships ?? []).length === 0 ? (
                <p className="text-white/50 text-sm py-4">No users linked yet.</p>
              ) : (
                (memberships ?? []).map((m) => {
                  const person = getPersonForUser(m.user_id)
                  const displayName = person
                    ? [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email
                    : m.user_id.slice(0, 8) + '…'
                  const displayEmail = person?.email ?? '—'
                  return (
                    <div
                      key={m.id}
                      className="flex items-center justify-between gap-4 py-3 px-4 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{displayName}</p>
                        <p className="text-sm text-white/60 truncate">{displayEmail}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <select
                          value={m.role}
                          onChange={(e) => updateRoleMutation.mutate({ membershipId: m.id, role: e.target.value })}
                          className="select-chevron-sm rounded-md border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white focus:border-white/40"
                          disabled={updateRoleMutation.isPending}
                        >
                          {BUSINESS_ROLES.map((r) => (
                            <option key={r.value} value={r.value} className="bg-slate-800 text-white">
                              {r.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-white/50 hover:text-amber-400 hover:bg-amber-500/10 h-8 w-8"
                          onClick={() => removeMemberMutation.mutate(m.id)}
                          disabled={removeMemberMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {availablePeople && availablePeople.length > 0 && (
              <div className="pt-4 border-t border-white/10">
                <Label className="text-white/80 mb-2 block">Add user</Label>
                <AddMemberForm
                  key={addFormKey}
                  people={availablePeople}
                  onAdd={(user_ids, role) => addMemberMutation.mutate({ user_ids, role })}
                  isSubmitting={addMemberMutation.isPending}
                />
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}

function AddMemberForm({
  people,
  onAdd,
  isSubmitting,
}: {
  people: PersonWithAuth[]
  onAdd: (user_ids: string[], role: string) => void
  isSubmitting: boolean
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedRole, setSelectedRole] = useState('business_user')
  const [search, setSearch] = useState('')

  const searchLower = search.trim().toLowerCase()
  const hasSearched = search.length > 0
  const filtered = searchLower
    ? people.filter(
        (p) =>
          [p.first_name, p.last_name].filter(Boolean).join(' ').toLowerCase().includes(searchLower) ||
          (p.email ?? '').toLowerCase().includes(searchLower)
      )
    : []

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const selectFiltered = () => {
    if (filtered.length === 0) return
    const ids = filtered.map((p) => p.auth_user_id!).filter(Boolean)
    setSelectedIds((prev) => new Set([...prev, ...ids]))
  }

  const clearSelection = () => setSelectedIds(new Set())

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    onAdd(ids, selectedRole)
    setSelectedIds(new Set())
    setSearch('')
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
          <Input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 focus:outline-none hover:border-white/30 focus:border-white/40 transition-all"
            disabled={isSubmitting}
          />
        </div>
        <div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="select-chevron h-10 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-white/40 transition-all"
            disabled={isSubmitting}
          >
            {BUSINESS_ROLES.map((r) => (
              <option key={r.value} value={r.value} className="bg-slate-800 text-white">
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          {hasSearched && filtered.length > 0 && (
            <button
              type="button"
              onClick={selectFiltered}
              className="text-sm text-white/60 hover:text-white"
            >
              Select filtered ({filtered.length})
            </button>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className="text-sm text-white/60 hover:text-white"
          >
            Clear
          </button>
        </div>
        <Button
          type="submit"
          disabled={isSubmitting || selectedIds.size === 0}
          size="sm"
          className="bg-white text-accent hover:bg-white/90"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : selectedIds.size === 0 ? 'Add users' : `Add ${selectedIds.size} user${selectedIds.size === 1 ? '' : 's'}`}
        </Button>
      </div>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10 bg-white/5 p-2 space-y-1">
        {filtered.length === 0 ? (
          <p className="text-sm text-white/50 py-4 text-center">
            {hasSearched ? 'No users match your search' : 'Search by name or email to find users'}
          </p>
        ) : (
          filtered.map((p) => {
            const checked = selectedIds.has(p.auth_user_id!)
            return (
              <label
                key={p.id}
                className={`flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer group transition-colors ${
                  checked ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
              >
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${
                  checked ? 'border-white bg-white' : 'border-white/40 bg-white/5 group-hover:border-white/60'
                }`}>
                  {checked && <Check className="h-3 w-3 text-accent stroke-[3] animate-check-pop" />}
                </span>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleUser(p.auth_user_id!)}
                  className="sr-only"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-white truncate min-w-0">
                  {[p.first_name, p.last_name].filter(Boolean).join(' ') || p.email}
                </span>
                <span className="text-sm text-white/50 truncate shrink-0">({p.email})</span>
              </label>
            )
          })
        )}
      </div>
      {search && filtered.length > 0 && (
        <p className="text-xs text-white/50">
          Showing {filtered.length} of {people.length} users
        </p>
      )}
    </form>
  )
}
